import type {
  AutoTaggingBundleTaskResponse,
  AutoTaggingPlan,
  ConfigurableConcept,
  ConceptValue,
} from "./autoTaggingService";

function clonePlan(plan: AutoTaggingPlan): AutoTaggingPlan {
  return {
    ...plan,
    concept_values: plan.concept_values.map((v) => ({ ...v })),
  };
}

const INITIAL_PLANS: AutoTaggingPlan[] = [
  {
    id: 12,
    target_type: "insight",
    sub_type: "insight",
    concept: "Country",
    concept_display_name: "Country",
    all_values: false,
    concept_values: [{ code: "DE", name: "Germany" }],
    prompt:
      "Tag DE only if the text cites a German authority (e.g. BfArM).",
    active: true,
  },
  {
    id: 13,
    target_type: "insight",
    sub_type: "insight",
    concept: "Country",
    concept_display_name: "Country",
    all_values: false,
    concept_values: [{ code: "FR", name: "France" }],
    prompt:
      "Tag FR only if the text cites a French authority (e.g. ANSM).",
    active: true,
  },
  {
    id: 14,
    target_type: "insight",
    sub_type: "insight",
    concept: "Therapeutic Area",
    concept_display_name: "Therapeutic Area",
    all_values: false,
    concept_values: [
      { code: "ONC", name: "Oncology" },
      { code: "CV", name: "Cardiovascular" },
      { code: "NEU", name: "Neurology" },
      { code: "IMM", name: "Immunology" },
    ],
    prompt:
      "Assess the therapeutic area related to this insight. Select only from the configured values: Oncology, Cardiovascular, Neurology, Immunology. If none are clearly applicable, return \"Not Applicable\".",
    active: true,
  },
  {
    id: 15,
    target_type: "insight",
    sub_type: "insight",
    concept: "Ingredient",
    concept_display_name: "Ingredient",
    all_values: false,
    concept_values: [
      { code: "PEM", name: "Pembrolizumab" },
      { code: "ADA", name: "Adalimumab" },
      { code: "SEM", name: "Semaglutide" },
    ],
    prompt:
      "Assess which ingredient or active substance this insight is related to. Select only from the configured values.",
    active: true,
  },
  {
    id: 16,
    target_type: "insight",
    sub_type: "insight",
    concept: "Health Authority",
    concept_display_name: "Health Authority",
    all_values: false,
    concept_values: [
      { code: "FDA", name: "FDA" },
      { code: "EMA", name: "EMA" },
      { code: "MHRA", name: "MHRA" },
    ],
    prompt:
      "Assess which health authority this insight is related to. Select only from: FDA, EMA, MHRA.",
    active: true,
  },
  {
    id: 17,
    target_type: "insight",
    sub_type: "insight",
    concept: "Product Type",
    concept_display_name: "Product Type",
    all_values: false,
    concept_values: [
      { code: "SM", name: "Small Molecule" },
      { code: "BIO", name: "Biologic" },
    ],
    prompt: "Assess which product type this object is related to.",
    active: false,
  },
];

let plansStore: AutoTaggingPlan[] = INITIAL_PLANS.map(clonePlan);

export const MOCK_CONFIGURABLE_CONCEPTS: ConfigurableConcept[] = [
  { code: "Country", name: "Country", display_name: "Country" },
  {
    code: "TherapeuticArea",
    name: "TherapeuticArea",
    display_name: "Therapeutic Area",
  },
  { code: "Ingredient", name: "Ingredient", display_name: "Ingredient" },
  {
    code: "HealthAuthority",
    name: "HealthAuthority",
    display_name: "Health Authority",
  },
  { code: "ProductType", name: "ProductType", display_name: "Product Type" },
];

export const MOCK_CONCEPT_VALUES: Record<string, ConceptValue[]> = {
  Country: [
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "US", name: "United States" },
    { code: "UK", name: "United Kingdom" },
  ],
  "Therapeutic Area": [
    { code: "ONC", name: "Oncology" },
    { code: "CV", name: "Cardiovascular" },
    { code: "NEU", name: "Neurology" },
    { code: "IMM", name: "Immunology" },
    { code: "END", name: "Endocrinology" },
  ],
  Ingredient: [
    { code: "PEM", name: "Pembrolizumab" },
    { code: "ADA", name: "Adalimumab" },
    { code: "SEM", name: "Semaglutide" },
    { code: "INS", name: "Insulin Glargine" },
  ],
  "Health Authority": [
    { code: "FDA", name: "FDA" },
    { code: "EMA", name: "EMA" },
    { code: "MHRA", name: "MHRA" },
    { code: "NMPA", name: "NMPA" },
  ],
  "Product Type": [
    { code: "SM", name: "Small Molecule" },
    { code: "BIO", name: "Biologic" },
    { code: "VAC", name: "Vaccine" },
  ],
};

const MOCK_DEFAULT_PROMPTS: Record<string, string> = {
  Country:
    "Please tag if the regulation applies to the specified country.",
  "Therapeutic Area":
    "Assess the therapeutic area related to this insight.",
  Ingredient:
    "Assess which ingredient or active substance this insight is related to.",
  "Health Authority":
    "Assess which health authority this insight is related to.",
  "Product Type": "Assess which product type this object is related to.",
};

function delay(ms = 200): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function resetAutoTaggingMockStore(): void {
  plansStore = INITIAL_PLANS.map(clonePlan);
}

export async function mockFetchConfigurableConcepts(): Promise<
  ConfigurableConcept[]
> {
  await delay();
  return MOCK_CONFIGURABLE_CONCEPTS.map((c) => ({ ...c }));
}

export async function mockFetchAutoTaggingPlans(
  targetType: string,
): Promise<AutoTaggingPlan[]> {
  await delay();
  return plansStore
    .filter((p) => p.target_type === targetType)
    .map(clonePlan);
}

export async function mockPatchAutoTaggingPlan(
  planId: number,
  payload: {
    concept?: string;
    all_values?: boolean;
    concept_values?: ConceptValue[];
    prompt?: string;
  },
): Promise<AutoTaggingPlan> {
  await delay();
  const index = plansStore.findIndex((p) => p.id === planId);
  if (index < 0) {
    throw new Error(`Plan ${planId} not found`);
  }
  const current = plansStore[index];
  const nextConcept = payload.concept ?? current.concept;
  const conceptConfig = MOCK_CONFIGURABLE_CONCEPTS.find(
    (concept) => concept.name === nextConcept,
  );
  const updated: AutoTaggingPlan = {
    ...current,
    concept: nextConcept,
    concept_display_name:
      conceptConfig?.display_name ?? current.concept_display_name,
    all_values: payload.all_values ?? current.all_values,
    concept_values:
      payload.concept_values?.map((v) => ({ ...v })) ??
      current.concept_values.map((v) => ({ ...v })),
    prompt: payload.prompt ?? current.prompt,
  };
  plansStore[index] = updated;
  return clonePlan(updated);
}

export async function mockPatchAutoTaggingPlanActive(
  planId: number,
  active: boolean,
): Promise<AutoTaggingPlan> {
  await delay();
  const index = plansStore.findIndex((p) => p.id === planId);
  if (index < 0) {
    throw new Error(`Plan ${planId} not found`);
  }
  plansStore[index] = { ...plansStore[index], active };
  return clonePlan(plansStore[index]);
}

export async function mockDeleteAutoTaggingPlan(planId: number): Promise<number> {
  await delay();
  const index = plansStore.findIndex((p) => p.id === planId);
  if (index < 0) {
    throw new Error(`Plan ${planId} not found`);
  }
  plansStore.splice(index, 1);
  return planId;
}

export async function mockFetchDefaultPrompt(
  concept: string,
): Promise<string> {
  await delay(100);
  return (
    MOCK_DEFAULT_PROMPTS[concept] ??
    "Please tag if the regulation applies to the specified concept."
  );
}

export async function mockTriggerAutoTaggingBundleTask(): Promise<AutoTaggingBundleTaskResponse> {
  await delay(300);
  return {
    uuid: `mock-bundle-${Date.now()}`,
    sub_tasks: ["mock-sub-1", "mock-sub-2"],
    completed_tasks: [],
    status: "running",
    trigger_by: "manual",
    force_update: false,
    total_tasks: 2,
    executed_at: new Date().toISOString(),
  };
}

export function mockGetConceptValues(concept: string): ConceptValue[] {
  return (MOCK_CONCEPT_VALUES[concept] ?? []).map((v) => ({ ...v }));
}
